import { useMemo } from 'react';

import debouncePromise from 'debounce-promise';
import type { GroupBase } from 'react-select-5';

import { FeatureRichSelectProps } from '../../index.types';

type Props<T extends FeatureRichSelectProps<any, any, any>> = T extends { async: true }
  ? Pick<T, 'async' | 'debounced' | 'debounceTimeout' | 'loadOptions'>
  : { async?: false };

export function useDebouncedLoadOptions<
  _Option = unknown,
  _IsMulti extends boolean = boolean,
  _Group extends GroupBase<_Option> = GroupBase<_Option>,
>(props: Props<FeatureRichSelectProps<_Option, _IsMulti, _Group>>) {
  const { async } = props;

  const debouncedAsyncLoadOptions = useMemo(() => {
    if (!async) return undefined;

    return props.loadOptions && props.debounced
      ? debouncePromise(props.loadOptions, props.debounceTimeout || 1000)
      : props.loadOptions;
  }, [async, async && props.debounced, async && props.debounceTimeout, async && props.loadOptions]);

  if (!async) return undefined;

  return (async (...p) => debouncedAsyncLoadOptions!(...p)) as typeof props.loadOptions;
}
